import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BadgeCheck, Check, RefreshCw, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Cartao, CorpoCartao, Esqueleto } from '@/componentes/ui/primitivos';

/**
 * Aprovar recebimento — a segunda metade do fluxo da conferência cega.
 *
 * Espelha o formulário `APROVACOES_RECEB_VOLUMES` do Procfit e fica na MESMA
 * tela da impressão, em outra aba, porque é o mesmo trabalho: imprime-se o
 * papel, o coletor conta, e alguém aprova. Duas entradas de menu para as duas
 * metades da mesma tarefa só fariam procurar.
 *
 * SÓ ENTRA O QUE FOI CONFERIDO
 * ----------------------------
 * A primeira versão listava também os recebimentos com zero bipes, marcados como
 * "não conferido", e deixava aprovar. O Procfit não deixa: medido em 27/10/2026,
 * os 542 recebimentos aprovados nos últimos 120 dias tinham bipe, sem exceção.
 * Aprovar sem contagem é assinar no escuro, então a fila (e a API) seguem a
 * regra do ERP.
 *
 * APROVAR PEDE O NÚMERO DIGITADO
 * ------------------------------
 * Aprovar grava no ERP e não tem desfazer nesta tela. Pedido do Pedro
 * (operação), 27/10/2026: um "Confirma?" com o mouse ainda deixa aprovar a
 * linha de cima por engano. Digitar o número do recebimento, como no Procfit,
 * obriga a olhar o papel conferido antes de assinar.
 */

export interface RecebimentoParaAprovar {
  recebimento: number;
  data: string | null;
  fornecedor: string | null;
  fornecedorCodigo: number | null;
  notaFiscal: number | null;
  contados: number;
  esperados: number;
}

interface AprovacaoGravada {
  aprovacao: number;
  recebimento: number;
  quando: string;
}

function dataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}

export function PainelAprovacao({
  centro,
  empresa,
}: {
  centro: number | null;
  empresa: number | null;
}): JSX.Element {
  const cliente = useQueryClient();
  const { temPermissao } = useSessao();
  const podeAprovar = temPermissao('recebimento.aprovar');

  const [confirmando, definirConfirmando] = React.useState<number | null>(null);
  const [erro, definirErro] = React.useState<string | null>(null);
  const [aprovados, definirAprovados] = React.useState<AprovacaoGravada[]>([]);

  const fila = useQuery<{ centro: number; pendentes: RecebimentoParaAprovar[] }>({
    queryKey: ['recebimentos-pendentes', centro],
    queryFn: () =>
      api.get<{ centro: number; pendentes: RecebimentoParaAprovar[] }>(
        '/api/recebimentos/aprovacoes/pendentes',
        { centro, limite: 60 },
      ),
    enabled: centro !== null,
    staleTime: 30_000,
  });

  const aprovar = useMutation({
    mutationFn: (recebimento: number) =>
      api.post<AprovacaoGravada>(`/api/recebimentos/${recebimento}/aprovar`, {
        centro,
        empresa,
      }),
    onSuccess: async (gravada) => {
      definirErro(null);
      definirConfirmando(null);
      // Fica na tela como recibo: sem isso, a linha some e não resta prova
      // visível de que a aprovação saiu — e a pessoa clica de novo.
      definirAprovados((antes) => [gravada, ...antes]);
      await cliente.invalidateQueries({ queryKey: ['recebimentos-pendentes'] });
      await cliente.invalidateQueries({ queryKey: ['recebimentos'] });
    },
    onError: (e) => {
      definirConfirmando(null);
      definirErro(e instanceof Error ? e.message : 'Não foi possível aprovar.');
    },
  });

  const pendentes = fila.data?.pendentes ?? [];

  return (
    <div className="space-y-3">
      {!podeAprovar ? (
        <Cartao>
          <CorpoCartao>
            <div className="flex items-start gap-2.5 text-[13px]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-atencao" />
              <p>
                Você pode ver a fila, mas não aprovar. A permissão{' '}
                <strong>Aprovar recebimento conferido</strong> é concedida em Administração &gt;
                Usuários.
              </p>
            </div>
          </CorpoCartao>
        </Cartao>
      ) : null}

      {erro ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-perigo/40 bg-perigo/10 px-3 py-2.5 text-[13px]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-perigo" />
          <span className="flex-1">{erro}</span>
          <button
            type="button"
            onClick={() => definirErro(null)}
            className="text-textoFraco hover:text-texto"
            aria-label="Fechar aviso"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {aprovados.length > 0 ? (
        <div className="rounded-xl border border-sucesso/40 bg-sucesso/10 px-3 py-2.5">
          <p className="flex items-center gap-1.5 text-[12.5px] font-bold text-sucesso">
            <BadgeCheck className="h-4 w-4" />
            {aprovados.length === 1
              ? '1 recebimento aprovado nesta sessão'
              : `${aprovados.length} recebimentos aprovados nesta sessão`}
          </p>
          <ul className="tabular mt-1 space-y-0.5 font-mono text-[11.5px] text-textoFraco">
            {aprovados.map((a) => (
              <li key={a.aprovacao}>
                Recebimento {a.recebimento} · aprovação {a.aprovacao} · {dataHora(a.quando)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Cartao className="flex min-h-0 flex-col">
        <div className="flex flex-wrap items-center gap-2 border-b border-borda px-4 py-2.5">
          <span className="text-[13px] font-bold">Aguardando aprovação</span>
          <span className="tabular text-[11.5px] text-textoFraco">
            {fila.isLoading ? '' : `${pendentes.length} conferido(s) nesta empresa`}
          </span>
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            className="ml-auto"
            onClick={() => void fila.refetch()}
            carregando={fila.isFetching}
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </Botao>
        </div>

        <CorpoCartao className="min-h-0 flex-1 overflow-y-auto">
          {fila.isLoading ? (
            <Esqueleto className="h-64" />
          ) : fila.isError ? (
            <EstadoErro erro={fila.error} aoTentarDeNovo={() => void fila.refetch()} />
          ) : pendentes.length === 0 ? (
            <EstadoVazio
              icone={<BadgeCheck className="h-7 w-7" />}
              titulo="Nada para aprovar"
              detalhe="Nenhum recebimento conferido dos últimos 60 dias desta empresa espera aprovação. Recebimento sem bipe do coletor só aparece aqui depois de conferido."
            />
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-borda text-textoFraco">
                  <th className="rotulo pb-1.5 pr-2">Recebimento</th>
                  <th className="rotulo pb-1.5 pr-2">Recebido em</th>
                  <th className="rotulo pb-1.5 pr-2">Fornecedor</th>
                  <th className="rotulo pb-1.5 pr-2">NF</th>
                  <th className="rotulo pb-1.5 pr-2 text-right">Conferência</th>
                  <th className="pb-1.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {pendentes.map((p) => (
                  <Linha
                    key={p.recebimento}
                    pendente={p}
                    podeAprovar={podeAprovar}
                    confirmando={confirmando === p.recebimento}
                    aprovando={aprovar.isPending && aprovar.variables === p.recebimento}
                    aoPedir={() => {
                      definirErro(null);
                      definirConfirmando(p.recebimento);
                    }}
                    aoDesistir={() => definirConfirmando(null)}
                    aoAprovar={() => aprovar.mutate(p.recebimento)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CorpoCartao>
      </Cartao>
    </div>
  );
}

function Linha({
  pendente,
  podeAprovar,
  confirmando,
  aprovando,
  aoPedir,
  aoDesistir,
  aoAprovar,
}: {
  pendente: RecebimentoParaAprovar;
  podeAprovar: boolean;
  confirmando: boolean;
  aprovando: boolean;
  aoPedir: () => void;
  aoDesistir: () => void;
  aoAprovar: () => void;
}): JSX.Element {
  const [digitado, definirDigitado] = React.useState('');
  const confere = digitado.trim() === String(pendente.recebimento);
  React.useEffect(() => {
    if (!confirmando) definirDigitado('');
  }, [confirmando]);

  return (
    <tr className={cn(confirmando && 'bg-atencao/10')}>
      <td className="tabular py-2 pr-2 font-mono text-[14px] font-bold">{pendente.recebimento}</td>
      <td className="tabular py-2 pr-2 text-[12px] text-textoFraco">{dataHora(pendente.data)}</td>
      <td className="max-w-[260px] truncate py-2 pr-2 text-[12.5px]" title={pendente.fornecedor ?? ''}>
        {pendente.fornecedor ?? '—'}
      </td>
      <td className="tabular py-2 pr-2 font-mono text-[12px]">{pendente.notaFiscal ?? '—'}</td>
      <td className="py-2 pr-2 text-right">
        <span className="tabular text-[12px]">
          <strong>{pendente.contados}</strong>
          <span className="text-textoFraco">
            {pendente.esperados > 0 ? ` / ${pendente.esperados}` : ''} itens
          </span>
        </span>
      </td>
      <td className="py-2 text-right">
        {!podeAprovar ? null : confirmando ? (
          <form
            className="inline-flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              if (confere && !aprovando) aoAprovar();
            }}
          >
            <input
              autoFocus
              inputMode="numeric"
              value={digitado}
              onChange={(e) => definirDigitado(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Escape') aoDesistir();
              }}
              placeholder="Digite o nº"
              aria-label={`Digite ${pendente.recebimento} para confirmar`}
              className={cn(
                'tabular h-8 w-28 rounded-lg border bg-superficie px-2 font-mono text-[13px] outline-none',
                digitado && !confere ? 'border-perigo' : 'border-borda focus:border-primaria',
              )}
            />
            <Botao
              type="submit"
              variante="primaria"
              tamanho="pequeno"
              disabled={!confere}
              carregando={aprovando}
            >
              <Check className="h-4 w-4" />
              Aprovar
            </Botao>
            <Botao type="button" variante="fantasma" tamanho="pequeno" onClick={aoDesistir}>
              Cancelar
            </Botao>
          </form>
        ) : (
          <Botao variante="secundaria" tamanho="pequeno" onClick={aoPedir} disabled={aprovando}>
            <BadgeCheck className="h-4 w-4" />
            Aprovar
          </Botao>
        )}
      </td>
    </tr>
  );
}
